import type { GraphNodeAnchor } from '../../entities/graph-document';
import { resolveSceneInteractionMode, type SceneInteractionMode } from './scene-interaction-mode';

export type NodePlacementSessionStatus = 'draft' | 'committed' | 'cancelled';

export interface NodePlacementSession {
	nodeId: string;
	startedAt: string;
	planeZ: number;
	startAnchor: GraphNodeAnchor;
	draftAnchor: GraphNodeAnchor;
	pointerId: number | null;
	status: NodePlacementSessionStatus;
}

export interface BeginNodePlacementSessionInput {
	mode: SceneInteractionMode;
	readonlyMode: boolean;
	nodeId: string;
	anchor: GraphNodeAnchor;
	planeZ: number;
	startedAt: string;
	pointerId?: number | null;
}

export function beginNodePlacementSession(
	activeSession: NodePlacementSession | null,
	input: BeginNodePlacementSessionInput,
): NodePlacementSession | null {
	if (activeSession?.status === 'draft') {
		return activeSession;
	}

	if (resolveSceneInteractionMode(input.mode, { readonlyMode: input.readonlyMode }) !== 'move-node') {
		return null;
	}

	return {
		nodeId: input.nodeId,
		startedAt: input.startedAt,
		planeZ: input.planeZ,
		startAnchor: { ...input.anchor },
		draftAnchor: { ...input.anchor },
		pointerId: input.pointerId ?? null,
		status: 'draft',
	};
}

export function updateNodePlacementSession(
	session: NodePlacementSession,
	nextAnchor: GraphNodeAnchor,
): NodePlacementSession {
	if (session.status !== 'draft') {
		return session;
	}

	return {
		...session,
		draftAnchor: { ...nextAnchor },
	};
}

export function commitNodePlacementSession(session: NodePlacementSession): NodePlacementSession {
	if (session.status !== 'draft') {
		return session;
	}

	return {
		...session,
		status: 'committed',
	};
}

export function cancelNodePlacementSession(session: NodePlacementSession): NodePlacementSession {
	if (session.status !== 'draft') {
		return session;
	}

	return {
		...session,
		status: 'cancelled',
		draftAnchor: { ...session.startAnchor },
	};
}

export function getCommittedPlacementAnchor(session: NodePlacementSession | null): GraphNodeAnchor | null {
	if (!session || session.status !== 'committed') {
		return null;
	}

	return { ...session.draftAnchor };
}

export function isActiveNodePlacementSession(session: NodePlacementSession | null): boolean {
	return session?.status === 'draft';
}